import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export interface RightPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * RightPanel - Reusable side panel component for detail views
 *
 * Usage:
 * <RightPanel open={isOpen} onOpenChange={setIsOpen} title="URL Details">
 *   <URLDetails data={selectedUrl} />
 *   <Notes data={selectedUrl} />
 *   <EvidenceCaptures data={selectedUrl} />
 * </RightPanel>
 */
export function RightPanel({
  open,
  onOpenChange,
  title,
  children,
  className,
}: RightPanelProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn("w-full sm:max-w-xl overflow-y-auto", className)}
        side="right"
      >
        <SheetHeader className="pb-6">
          <SheetTitle className="text-xl font-semibold">{title}</SheetTitle>
        </SheetHeader>
        <div className="space-y-6">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * RightPanelSection - Container for a section within the right panel
 */
interface RightPanelSectionProps {
  children: React.ReactNode;
  className?: string;
}

export function RightPanelSection({
  children,
  className,
}: RightPanelSectionProps) {
  return <div className={cn("space-y-3", className)}>{children}</div>;
}

/**
 * RightPanelSectionTitle - Title for a panel section
 */
interface RightPanelSectionTitleProps {
  children: React.ReactNode;
  className?: string;
}

export function RightPanelSectionTitle({
  children,
  className,
}: RightPanelSectionTitleProps) {
  return (
    <h3 className={cn("text-sm font-semibold text-foreground", className)}>
      {children}
    </h3>
  );
}

/**
 * RightPanelField - Label-value pair display
 */
interface RightPanelFieldProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

export function RightPanelField({
  label,
  value,
  className,
}: RightPanelFieldProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  );
}

/**
 * RightPanelGrid - Grid layout for fields
 */
interface RightPanelGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}

export function RightPanelGrid({
  children,
  columns = 2,
  className,
}: RightPanelGridProps) {
  return (
    <div
      className={cn(
        "grid gap-4",
        columns === 1 && "grid-cols-1",
        columns === 2 && "grid-cols-2",
        columns === 3 && "grid-cols-3",
        className,
      )}
    >
      {children}
    </div>
  );
}


